
import { trackButtonClick } from '@/utils/analytics';
import React, { useState, useEffect, useRef } from 'react';
import { BiFontFamily } from 'react-icons/bi';

const CalendarDropdown = ({
  title,
  description,
  location,
  startDateTime,
  endDateTime,
  fileName = 'event',
  buttonLabel = 'Add to Calendar'
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const dropdownRef = useRef(null);



function formatICSDate(date) {
  const parsed = (date instanceof Date) ? date : new Date(date);
  if (isNaN(parsed)) {
    console.error("Invalid date for ICS:", date);
    return "";
  }
  return parsed.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

function formatGoogleDate(date) {
  const parsed = (date instanceof Date) ? date : new Date(date);
  if (isNaN(parsed)) {
    console.error("Invalid date for Google Calendar:", date);
    return "";
  }
  return parsed.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

const googleCalendarUrl = () => {
  const start = formatGoogleDate(startDateTime);
  const end = formatGoogleDate(endDateTime);
  const url = new URL('https://calendar.google.com/calendar/render');
  url.searchParams.set('action', 'TEMPLATE');
  url.searchParams.set('text', title);
  url.searchParams.set('dates', `${start}/${end}`);
  url.searchParams.set('details', description);
  url.searchParams.set('location', location);
  return url.toString();
};


function escapeICSString(str) {
  return str
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

function foldLine(line) {
  return line.match(/.{1,75}/g)?.join('\r\n ') || line;
}


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const escapedDescription = escapeICSString(description);
const foldedDescription = foldLine(`DESCRIPTION:${escapedDescription}`);


  const downloadICS = (type) => {
    const icsContent = `
BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Your Company//Your Product//EN
BEGIN:VEVENT
UID:${Date.now()}@yourdomain.com
DTSTAMP:${formatICSDate(new Date())}
DTSTART:${formatICSDate(startDateTime)}
DTEND:${formatICSDate(endDateTime)}
SUMMARY:${title}
${foldedDescription}
LOCATION:${location}
END:VEVENT
END:VCALENDAR
    `.trim();

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    trackButtonClick({
      eventName: "add_to_calendar_option_click",
      eventParams: { calendar_type: type === "APPLE" ? "apple" : "outlook" },
      eventCategory: "NEW-PDP",
      eventSource: `Add To Calendar ${type === "APPLE" ? "Apple" : "Outlook"} Click`,
    })
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => {
          setShowOptions(!showOptions)
          trackButtonClick({
            eventName: "add_to_calendar_click",
            eventCategory: "NEW-PDP",
            eventSource: "Click",
          })
        }}
        style={mainButtonStyle}
      >
        {buttonLabel}
      </button>

      {showOptions && (
        <div style={dropdownStyle}>
          <a
            href={googleCalendarUrl()}
            target="_blank"
            rel="noopener noreferrer"
            style={dropdownItemStyle}
            onClick={() => {
              trackButtonClick({
                eventName: "add_to_calendar_google_click",
                eventCategory: "NEW-PDP",
                eventSource: "Add To Calendar Google Click",
              })
            }}
          >
            <img
              src="/assets/images/google_cal.png"
              alt="Google"
              style={iconStyle}
            />
            Google
          </a>
          <div onClick={() => downloadICS("APPLE")} style={dropdownItemStyle}>
            <img
              src="/assets/images/apple_cal.png"
              alt="Apple"
              style={iconStyle}
            />
            Calendar
          </div>
          <div onClick={() => downloadICS("OUTLOOK")} style={dropdownItemStyle}>
            <img
              src="/assets/images/outlook_cal.png"
              alt="Outlook"
              style={iconStyle}
            />
            Outlook
          </div>
        </div>
      )}
    </div>
  );
};

const mainButtonStyle = {
  padding: '10px 16px',
  color: '#1a73e8',
  backgroundColor: '#fff',
  border: '1px solid #1a73e8',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 500,
  fontSize: '14px',
  fontFamily: "graphikmedium",
  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
  transition: 'all 0.2s ease'
};


const dropdownStyle = {
  position: 'absolute',
  top: '110%',
  left: 0,
  backgroundColor: '#fff',
  border: '1px solid #ddd',
  borderRadius: '6px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  zIndex: 1000,
  minWidth: '140px',
  overflow: 'hidden'
};

const dropdownItemStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '10px 15px',
  cursor: 'pointer',
  fontSize: '14px',
  color: '#333',
  textDecoration: 'none',
  transition: 'background-color 0.2s ease',
  borderBottom: '1px solid #eee'
};

const iconStyle = {
  marginRight: '10px',
  width: '20px',
  height: '20px'
};

export default CalendarDropdown;
